/**
 * Hooks TanStack Query — génération batch (S5.5 phase 2).
 *
 * Pattern :
 *   - `useMissingDrafts` : alimente le badge `/drafts` (Feature D).
 *     staleTime 60s + refetch au focus tab pour rester frais sans spammer.
 *   - `useEnqueueGenerationBatch` : crée un batch ; invalide les drafts
 *     (qui vont changer post-batch) + missing-drafts + batch status.
 *   - `useGenerationBatchStatus` : polling 2s pendant in_progress
 *     (running > 0 OR scheduled > 0) ; stop sinon. Pattern aligné sur
 *     les hooks queue de S5b.
 *   - `useGenerationJob` : détail d'un job (modal d'erreurs).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { draftsKeys } from '@/hooks/useDrafts'
import type {
  BatchStatusResponse,
  EnqueueBatchRequest,
  EnqueueBatchResponse,
  GenerationJobResponse,
  WorkMissingDraftsResponse,
} from '@/types/generation-jobs'
import { generationJobsApi } from '@/utils/api/generation-jobs'

// ──────────────────────────────────────────────────────────────────────────────
// Query keys
// ──────────────────────────────────────────────────────────────────────────────

export const generationJobsKeys = {
  all: ['generation-jobs'] as const,
  missingDrafts: () => [...generationJobsKeys.all, 'missing-drafts'] as const,
  batch: (batchId: string) => [...generationJobsKeys.all, 'batch', batchId] as const,
  detail: (jobId: number) => [...generationJobsKeys.all, 'detail', jobId] as const,
}

// ──────────────────────────────────────────────────────────────────────────────
// Feature D — badge /drafts "X drafts manquants"
// ──────────────────────────────────────────────────────────────────────────────

export function useMissingDrafts() {
  return useQuery<WorkMissingDraftsResponse>({
    queryKey: generationJobsKeys.missingDrafts(),
    queryFn: generationJobsApi.listMissingDrafts,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  })
}

// ──────────────────────────────────────────────────────────────────────────────
// Feature B + D — enqueue batch
// ──────────────────────────────────────────────────────────────────────────────

export function useEnqueueGenerationBatch() {
  const qc = useQueryClient()
  return useMutation<EnqueueBatchResponse, Error, EnqueueBatchRequest>({
    mutationFn: generationJobsApi.enqueueBatch,
    onSuccess: () => {
      // Drafts vont être créés en arrière-plan → invalide la liste + le
      // compteur pending. Le status batch sera poll dédié.
      qc.invalidateQueries({ queryKey: draftsKeys.all })
      qc.invalidateQueries({ queryKey: generationJobsKeys.missingDrafts() })
    },
  })
}

// ──────────────────────────────────────────────────────────────────────────────
// Suivi du batch (modal de progression)
// ──────────────────────────────────────────────────────────────────────────────

export function useGenerationBatchStatus(batchId: string | null) {
  return useQuery<BatchStatusResponse>({
    queryKey: generationJobsKeys.batch(batchId ?? ''),
    queryFn: () => generationJobsApi.getBatchStatus(batchId!),
    enabled: batchId != null && batchId.length > 0,
    // Polling 2s pendant in_progress (running > 0 OR scheduled > 0). Stop
    // dès que tout est terminé (done + failed + cancelled).
    refetchInterval: (query) => {
      const data = query.state.data
      if (!data) return 2_000
      const inProgress = data.running > 0 || data.scheduled > 0
      return inProgress ? 2_000 : false
    },
  })
}

export function useGenerationJob(jobId: number | null | undefined) {
  return useQuery<GenerationJobResponse>({
    queryKey: generationJobsKeys.detail(jobId ?? 0),
    queryFn: () => generationJobsApi.getJob(jobId!),
    enabled: jobId != null && jobId > 0,
  })
}
